/**
 * Page Footer — mobile accordion (progressive enhancement).
 *
 * Below the md breakpoint each footer column heading is turned into a button
 * that toggles its list panel. The markup ships fully expanded and unenhanced,
 * so without JS — or on desktop — every column stays visible and the desktop
 * grid is untouched. The enhancement is built when the viewport is below md and
 * fully torn down again above it, and it supports several footer instances on
 * one page (each column gets unique ids). Keyboard support follows the WAI-ARIA
 * accordion pattern (Up/Down between triggers, Home/End to the ends).
 */

const MOBILE_QUERY = '(max-width: 767.98px)';
const FOOTER_SELECTOR = '.b-page-footer';
const COLUMN_SELECTOR = '.b-page-footer__column';
const HEADING_SELECTOR = '.wp-block-heading';
const PANEL_SELECTOR = '.wp-block-list';
const CHEVRON_FILENAME = 'page-footer__chevron.svg';

// Unique id counter shared across all instances on the page.
let uid = 0;

/**
 * Build the chevron icon used inside an accordion trigger. Rendered as an
 * <img> (mirrors the Chevron in assets.js) so every trigger — across all
 * columns and every footer instance on the page — points at the same file
 * URL and the browser fetches it once and serves the rest from cache,
 * instead of shipping duplicate inline SVG markup in the DOM. CSS rotates it
 * when expanded.
 *
 * @param {string} iconsUrl Base URL for the page-footer image assets (read
 * from the footer's data-icons-url attribute).
 * @return {HTMLImageElement} Chevron <img> element.
 */
const createChevron = ( iconsUrl ) => {

	const img = document.createElement( 'img' );
	img.className = 'b-page-footer__chevron';
	img.src = `${ iconsUrl }/${ CHEVRON_FILENAME }`;
	img.width = 24;
	img.height = 24;
	img.alt = '';
	img.setAttribute( 'aria-hidden', 'true' );
	img.loading = 'lazy';
	img.decoding = 'async';

	return img;

};

/**
 * Accordion enhancement for a single footer instance. Owns the triggers it
 * builds and can fully restore the original markup on teardown.
 */
class FooterAccordion {

	/**
	 * @param {HTMLElement} footer The .b-page-footer element to enhance.
	 */
	constructor( footer ) {

		this.footer = footer;
		this.columns = [];
		this.iconsUrl = footer.dataset.iconsUrl || '';
		this.onKeydown = this.onKeydown.bind( this );

		Array.from( footer.querySelectorAll( COLUMN_SELECTOR ) ).forEach(
			( column ) => {

				const controller = this.enhanceColumn( column );
				if ( controller ) {

					this.columns.push( controller );

				}

			}
		);

	}

	/**
	 * Turn one column into a trigger + collapsible panel.
	 *
	 * @param {HTMLElement} column The .b-page-footer__column element.
	 * @return {?object} Column controller, or null when there is nothing to enhance.
	 */
	enhanceColumn( column ) {

		const heading = column.querySelector( HEADING_SELECTOR );
		const panel = column.querySelector( PANEL_SELECTOR );
		if ( ! heading || ! panel ) {

			return null;

		}

		uid += 1;
		const triggerId = `page-footer-trigger-${ uid }`;
		const panelId = `page-footer-panel-${ uid }`;

		const trigger = document.createElement( 'button' );
		trigger.type = 'button';
		trigger.className = 'b-page-footer__accordion-trigger';
		trigger.id = triggerId;
		trigger.setAttribute( 'aria-controls', panelId );

		// Put the trigger where the heading was, then move the heading inside
		// it (the CSS styles the heading as a descendant of the trigger).
		column.insertBefore( trigger, heading );
		trigger.appendChild( heading );
		trigger.appendChild( createChevron( this.iconsUrl ) );

		panel.id = panelId;
		panel.setAttribute( 'role', 'region' );
		panel.setAttribute( 'aria-labelledby', triggerId );

		const controller = { column, heading, panel, trigger };
		controller.onClick = () => this.toggle( controller );
		trigger.addEventListener( 'click', controller.onClick );
		trigger.addEventListener( 'keydown', this.onKeydown );

		// Initial state from the server-rendered data-default-open flag.
		this.setOpen( controller, column.dataset.defaultOpen === 'true' );

		return controller;

	}

	/**
	 * Apply the open/closed state to a column.
	 *
	 * @param {object}  controller Column controller.
	 * @param {boolean} open       Whether the panel should be expanded.
	 */
	setOpen( controller, open ) {

		const { column, panel, trigger } = controller;
		trigger.setAttribute( 'aria-expanded', open ? 'true' : 'false' );
		column.classList.toggle( 'is-open', open );
		if ( open ) {

			panel.removeAttribute( 'hidden' );

		} else {

			panel.setAttribute( 'hidden', '' );

		}

	}

	/**
	 * Toggle a column open/closed.
	 *
	 * @param {object} controller Column controller.
	 */
	toggle( controller ) {

		const open =
			controller.trigger.getAttribute( 'aria-expanded' ) === 'true';
		this.setOpen( controller, ! open );

	}

	/**
	 * Move focus between triggers with the arrow / Home / End keys.
	 *
	 * @param {KeyboardEvent} event Keydown event on a trigger.
	 */
	onKeydown( event ) {

		const triggers = this.columns.map( ( c ) => c.trigger );
		const index = triggers.indexOf( event.currentTarget );
		if ( index === -1 ) {

			return;

		}

		let next;
		switch ( event.key ) {

		case 'ArrowDown':
			next = ( index + 1 ) % triggers.length;
			break;
		case 'ArrowUp':
			next = ( index - 1 + triggers.length ) % triggers.length;
			break;
		case 'Home':
			next = 0;
			break;
		case 'End':
			next = triggers.length - 1;
			break;
		default:
			return;

		}

		event.preventDefault();
		triggers[ next ].focus();

	}

	/**
	 * Restore the original markup: drop the triggers, move the headings back
	 * and reveal every panel. Leaves the column exactly as server-rendered.
	 */
	destroy() {

		this.columns.forEach( ( controller ) => {

			const { column, heading, panel, trigger } = controller;
			trigger.removeEventListener( 'click', controller.onClick );
			trigger.removeEventListener( 'keydown', this.onKeydown );

			column.insertBefore( heading, trigger );
			trigger.remove();

			column.classList.remove( 'is-open' );
			panel.removeAttribute( 'hidden' );
			panel.removeAttribute( 'role' );
			panel.removeAttribute( 'aria-labelledby' );
			panel.removeAttribute( 'id' );

		} );
		this.columns = [];

	}

}

/**
 * Wire the accordion up to the md media query: build it below md, tear it down
 * above md, for every footer on the page.
 */
const init = () => {

	const footers = Array.from( document.querySelectorAll( FOOTER_SELECTOR ) );
	if ( ! footers.length ) {

		return;

	}

	const mql = window.matchMedia( MOBILE_QUERY );
	const instances = new WeakMap();

	/**
	 * Build or tear down each footer's accordion to match the current viewport.
	 */
	const sync = () => {

		footers.forEach( ( footer ) => {

			const existing = instances.get( footer );
			if ( mql.matches && ! existing ) {

				instances.set( footer, new FooterAccordion( footer ) );

			} else if ( ! mql.matches && existing ) {

				existing.destroy();
				instances.delete( footer );

			}

		} );

	};

	sync();
	mql.addEventListener( 'change', sync );

};

if ( document.readyState !== 'loading' ) {

	init();

} else {

	document.addEventListener( 'DOMContentLoaded', init );

}
